import { Form, Modal, Typography } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useGetDetailBankAcccount } from "../../api/bankAccount";
import {
  useCreateCompany,
  useGetCompanyDefaultMutation,
  useGetDetailCompany,
} from "../../api/company";
import { getErrorMessage, isNotFoundError } from "../../api/error";
import { useGetDetailSendMoney, useGetOrderDetailSendMoney } from "../../api/send-money";
import { useGetDetailWallet } from "../../api/wallet";
import { ReactComponent as CloseIcon } from "../../common/images/svg/CloseIcon.svg";
import { ReactComponent as PrinterIcon } from "../../common/images/svg/Printer.svg";
import CustomBreadCrumb from "../../components/breadCrumb/breadCrumb";
import { useNotification } from "../../components/providers/notification/useNotification";
import { setTitleHeader } from "../../store/use-title-header-store";
import PaymentStatus from "../../types/paymentStatus.type";
import { canCreateNewOrder } from "../../utils/canCreateNewOrder";
import { useAllMetadataCurrency } from '../../utils/hooks/useAllMetadataCurrency';
import { useBankAccountManagement } from "../../utils/hooks/useBankAccount";
import { useBankAccountManagementProvider } from "../../utils/hooks/useBankAccountProvider";
import { useCompany } from "../../utils/hooks/useCompany";
import { useMetadataCompaniesDefault } from "../../utils/hooks/useMetadataCompaniesDefault";
import { useMetadataCountries } from "../../utils/hooks/useMetadataCountries";
import { useMetadataCurrency } from "../../utils/hooks/useMetadataCurrency";
import { useReloadSocket } from "../../utils/hooks/useReloadSocket";
import useResponsive from "../../utils/hooks/useResponsive";
import { useWalletManagement } from "../../utils/hooks/useWalletManagement";
import { useWalletManagementProvider } from "../../utils/hooks/useWalletManagementProvider";
import { listStep } from "../benificiary-remitter/management/benificiary";
import FormBank from "../benificiary-remitter/management/modal-add-new/new-payee/form-bank";
import FormConfirm from "../benificiary-remitter/management/modal-add-new/new-payee/form-confirm";
import FormInfo from "../benificiary-remitter/management/modal-add-new/new-payee/form-info";
import FormWallet from "../benificiary-remitter/management/modal-add-new/new-payee/form-wallet";
import TitleModal from "../benificiary-remitter/management/modal-add-new/new-payee/title-modal";
import {
  BankBeneficiary,
  InfoBeneficiary,
  WalletBeneficiary,
} from "../benificiary-remitter/type";
import DirectorTransferDetailV2 from "./component/director-transfer-details-v2";
import FormAddBankAccount from "./component/form-bank";
import FormFutureDate from "./component/form-future-date";
import { Fiat, listPaymentMethod } from "./component/form-transfer-props";
import FormTransferV2 from "./component/form-transfer-v2";
import FormAddWallet from "./component/form-wallet";
import "./send-money.css";

const breadCrumb = [
  {
    name: "Portfolio",
    path: "/",
  },
  {
    name: "Send Money",
    path: "/send-money",
  },
];

const start = 0;
const end = 10;

export const ThemedSendMoney: React.FC<any> = () => {
  const [formSendMoney] = Form.useForm();
  const [formFutureDate] = Form.useForm();
  const socket = useReloadSocket();
  const canAction = canCreateNewOrder();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const currencyDefault = queryParams.get("currency");

  const orderId = queryParams.get("orderId");

  const {
    data: dataSendMoney,
    refetch: refetchDetailSendMoney,
    isFetching: loadingDetailSendMoney,
    isRefetching: refetchingDetailSendMoney,
    isError: isOrderDetailError,
    error: orderDetailError,
  } = useGetOrderDetailSendMoney(orderId)

  const { isTablet } = useResponsive();
  const navigate = useNavigate();
  const { open: openNotification } = useNotification();

  useEffect(() => {
    if (
      orderId &&
      dataSendMoney?.data &&
      dataSendMoney?.data?.status &&
      dataSendMoney?.data?.status?.current !== PaymentStatus.CREATED
    ) {
      const message = "This order cannot be edit."
      openNotification?.({
        key: "order_detail_error",
        message,
        type: "error",
      })
      const isFiat = Fiat.includes(dataSendMoney?.data?.paymentCurrency);
      navigate(`/history?tab=${isFiat ? "fiat" : "crypto"}&orderId=${orderId}&type=send-money`);
    }
  }, [orderId, dataSendMoney, openNotification, navigate])

  useEffect(() => {
    if (orderId && isOrderDetailError) {
      if (isNotFoundError(orderDetailError)) {
        navigate("/404")
      } else {
        openNotification?.({
          key: "order_detail_error",
          message: getErrorMessage(orderDetailError) || "Failed to load order detail",
          type: "error",
        })
        navigate("/send-money")
      }
    }
  }, [orderId, isOrderDetailError, orderDetailError, openNotification, navigate])

  const hasRunRef = useRef(false);
  const [openModalDriectorTransfer, setOpenModalDirectorTransfer] =
    useState(false);

  const [paymentMethod, setPaymentMethod] = useState<any>(
    listPaymentMethod.PORTFOLIO
  );
  const [detailSendMoney, setDetailSendMoney] = useState<any>(null);
  const [openAddWallet, setOpenAddWallet] = useState(false);
  const [openAddBankAccount, setOpenAddBankAccount] = useState(false);
  const [stepNewBeneficiary, setStepNewBeneficiary] = useState<number | null>(
    null
  );
  const [bankSelected, setBankSelected] = useState<any>();
  const [walletSkip, setWalletSkip] = useState<boolean>(false);
  const [bankSkip, setBankSkip] = useState<boolean>(false);
  const [valueFormInfo, setValueFormInfo] = useState<InfoBeneficiary>();
  const [valueFormWallet, setValueFormWallet] = useState<
    WalletBeneficiary | undefined
  >();

  const [valueFormBank, setValueFormBank] = useState<
    BankBeneficiary | undefined
  >();
  const [infoByUserCode, setInfoByUserCode] = useState<any>(null);
  const [sendCurrency, setSendCurrency] = useState<number | undefined>();
  const [paymentCurrency, setPaymentCurrency] = useState<number | undefined>();

  const [defaultBeneficiary, setDefaultBeneficiary] = useState(null);

  const [paramsBeneficiary, setParamsBeneficiary] = useState({ start, end });
  const [searchBeneficiary, setSearchBeneficiary] = useState();
  const [beneficiarySelected, setBeneficiarySelected] = useState<any>(null);
  const [actionFutureDate, setActionFutureDate] = useState<boolean>(false);

  const { metadataCountries } = useMetadataCountries();
  const { metadataCurrency } = useMetadataCurrency();

  const { dataAllCurrency } = useAllMetadataCurrency()

  const { dataListCompany: dataListCompanyDefault, refetch, isLoading } = useCompany(
    paramsBeneficiary,
    searchBeneficiary,
    undefined,
    defaultBeneficiary ? String(defaultBeneficiary) : undefined
  );

  const { data: dataDefaultBeneficiary, isLoading: isLoadingDefaultBeneficiary } =
    useGetDetailCompany(defaultBeneficiary ? Number(defaultBeneficiary) : null);

  const dataListCompany = useMemo(() => {
    const baseItems = (dataListCompanyDefault?.items as any[]) || [];

    const dataCompany = {
      items: baseItems,
      total: dataListCompanyDefault?.total || 0,
      page: dataListCompanyDefault?.page || 0,
      pageSize: dataListCompanyDefault?.pageSize || 0,
      totalPages: dataListCompanyDefault?.totalPages || 0,
    };

    if (dataDefaultBeneficiary?.data) {
      const defaultData = dataDefaultBeneficiary.data;
      const filteredItems = baseItems.filter(
        (item: any) => item?.id !== defaultData?.id
      );

      return {
        ...dataCompany,
        items: [defaultData, ...filteredItems],
      } as any;
    }

    return dataCompany as any;
  }, [dataListCompanyDefault, dataDefaultBeneficiary]);

  const [editData, setEditData] = useState<any>(null);

  const [defaultBeneficiaryWalletId, setDefaultBeneficiaryWalletId] = useState<number | null>(null)

  const [defaultBeneficiaryBankAccountId, setDefaultBeneficiaryBankAccountId] = useState<number | null>(null)

  // When editing order: derive default wallet & bank account IDs from beneficiary
  useEffect(() => {
    const ben = editData?.beneficiary;

    if (!ben) {
      setDefaultBeneficiaryWalletId(null);
      setDefaultBeneficiaryBankAccountId(null);
      return;
    }

    const benWalletId = ben.walletId ?? ben.wallet_id;
    setDefaultBeneficiaryWalletId(
      benWalletId === null || benWalletId === undefined
        ? null
        : Number(benWalletId)
    );

    const benBankAccountId = ben.bankAccountId;
    setDefaultBeneficiaryBankAccountId(
      benBankAccountId === null || benBankAccountId === undefined
        ? null
        : Number(benBankAccountId)
    );
  }, [editData?.beneficiary]);

  // Fetch detail of default wallet / bank account for edit mode
  const { data: dataDefaultWallet } = useGetDetailWallet(
    defaultBeneficiaryWalletId ? Number(defaultBeneficiaryWalletId) : 0
  );

  const { data: dataDefaultBankAccount } = useGetDetailBankAcccount(
    defaultBeneficiaryBankAccountId ? Number(defaultBeneficiaryBankAccountId) : 0
  );

  const [paramsWallet, setParamsWallet] = useState({ start, end });
  const [searchWallet, setSearchWallet] = useState();
  const [walletSelected, setWalletSelected] = useState<any>(null);

  const { dataListWallet, refetch: refectListWallet, isLoading: loadingListWallet } = useWalletManagement(
    Number(beneficiarySelected?.id || 0),
    paramsWallet,
    searchWallet,
    sendCurrency,
    defaultBeneficiaryWalletId ? String(defaultBeneficiaryWalletId) : undefined
  );

  const [paramsBankAccount, setParamsBankAccount] = useState({ start, end });
  const [searchBankAccount, setSearchBankAccount] = useState();
  const [bankAccountSelected, setBankAccountSelected] = useState<any>(null);

  const { dataListBankAccount, refetch: refectListBankAccount, isLoading: loadingListBankAccount } =
    useBankAccountManagement(
      Number(beneficiarySelected?.id || 0),
      paramsBankAccount,
      searchBankAccount,
      sendCurrency,
      defaultBeneficiaryBankAccountId ? String(defaultBeneficiaryBankAccountId) : undefined
    );

  // Combine list bank account with default bank account (ignore duplicate by id, put default on top)
  const dataListBankAccountWithDefault = useMemo(() => {
    const baseItems = (dataListBankAccount?.items as any[]) || [];

    const dataBankAccount = dataDefaultBankAccount?.data;

    const dataBankList = {
      items: baseItems,
      total: dataListBankAccount?.total || 0,
      page: dataListBankAccount?.page || 0,
      pageSize: dataListBankAccount?.pageSize || 0,
      totalPages: dataListBankAccount?.totalPages || 0,
    };

    if (dataBankAccount && defaultBeneficiaryBankAccountId && defaultBeneficiaryBankAccountId) {
      const filteredItems = baseItems.filter(
        (item: any) => Number(item?.id) !== Number(defaultBeneficiaryBankAccountId)
      );

      return {
        ...dataBankList,
        items: [dataBankAccount, ...filteredItems],
      } as any;
    }

    return dataBankList as any;
  }, [dataListBankAccount, dataDefaultBankAccount, defaultBeneficiaryBankAccountId]);

  // Combine list wallet with default wallet (ignore duplicate by id, put default on top)
  const dataListWalletWithDefault = useMemo(() => {
    const baseItems = (dataListWallet?.items as any[]) || [];

    const dataWallet = dataDefaultWallet?.data?.data;

    const dataWalletList = {
      items: baseItems,
      total: dataListWallet?.total || 0,
      page: dataListWallet?.page || 0,
      pageSize: dataListWallet?.pageSize || 0,
      totalPages: dataListWallet?.totalPages || 0,
    };

    if (dataWallet && defaultBeneficiaryWalletId && defaultBeneficiaryWalletId) {
      const filteredItems = baseItems.filter(
        (item: any) => Number(item?.id) !== Number(defaultBeneficiaryWalletId)
      );

      return {
        ...dataWalletList,
        items: [dataWallet, ...filteredItems],
      } as any;
    }

    return dataWalletList as any;
  }, [dataListWallet, dataDefaultWallet, defaultBeneficiaryWalletId]);

  const [companyDefaultSelected, setCompanyDefaultSelected] =
    useState<any>(null);

  const [companyDefaultCurrency, setCompanyDefaultCurrency] =
    useState<any>(null);

  const [disabledPortfolio, setDisabledPortfolio] = useState(false);

  const [searchDefaultCompany, setSearchDefaultCompany] = useState<any>();

  const { dataCompaniesDefault, isLoading: loadingDefaulCompany } =
    useMetadataCompaniesDefault({
      currencyId: paymentCurrency,
      searchName: searchDefaultCompany,
    });

  const { mutate: getCompanyDefault, isLoading: loadingCompanyCurrency } =
    useGetCompanyDefaultMutation({
      onSuccess: (response: { data: [] }) => {
        setCompanyDefaultCurrency(response?.data);
      },
    });

  const [reloadRateFeeKey, setReloadRateFeeKey] = useState(0);

  useEffect(() => {
    if (!paymentCurrency) return;
    getCompanyDefault({ currencyId: paymentCurrency });
  }, [paymentCurrency]);

  const [paramsWalletProvider, setParamsWalletProvider] = useState({
    start,
    end,
  });
  const [searchWalletProvider, setSearchWalletProvider] = useState();
  const [walletSelectedProvider, setWalletSelectedProvider] =
    useState<any>(null);

  const { dataListWalletProvider } = useWalletManagementProvider(
    Number(companyDefaultSelected?.id || 0),
    paramsWalletProvider,
    searchWalletProvider,
    paymentCurrency
  );

  const [paramsBankAccountProvider, setParamsBankAccountProvider] = useState({
    start,
    end,
  });
  const [searchBankAccountProvider, setSearchBankAccountProvider] = useState();
  const [bankAccountSelectedProvider, setBankAccountSelectedProvider] =
    useState<any>(null);

  const { dataListBankAccountProvider } = useBankAccountManagementProvider(
    Number(companyDefaultSelected?.id || 0),
    paramsBankAccountProvider,
    searchBankAccountProvider,
    paymentCurrency
  );

  const { mutate: createCompany, isLoading: loadingCreateCompany } =
    useCreateCompany({
      onSuccess: (response: { data: { message: string; data: any } }) => {
        if (openNotification) {
          openNotification({
            key: "create_success",
            message:
              response.data.message || "Create Beneficiary successfully!",
            type: "success",
          });
        }
        setValueFormInfo(undefined);
        setValueFormWallet(undefined);
        setValueFormBank(undefined);
        setStepNewBeneficiary(null);
        setBankSelected(null);
        setWalletSkip(false);
        setBankSkip(false);
        refetch();
        formSendMoney?.setFieldsValue({
          beneficiary: response?.data?.data?.id,
        });
        setWalletSelected(null);
        setBankAccountSelected(null);
        formSendMoney?.resetFields([
          "beneficiary_wallet_address",
          "beneficiary_bank_account",
        ]);
      },
      onError: (error: { message: string }) => {
        if (openNotification) {
          openNotification({
            key: "create_failed",
            message: error.message || "Create Beneficiary failed!",
            type: "error",
          });
        }
      },
    });

  const { mutate: getDetailSendMoney, isLoading: loadingDetail } =
    useGetDetailSendMoney({
      onSuccess: (response: { data: any }) => {
        setDetailSendMoney(response?.data);
        setOpenModalDirectorTransfer(true);
      },
      onError: (error: { message: string }) => {
        if (openNotification) {
          openNotification({
            key: "create_failed",
            message: "Something went wrong!",
            type: "error",
          });
        }
      },
    });

  const handleCloseModalBeneficiary = () => {
    if (loadingCreateCompany) return;
    setValueFormInfo(undefined);
    setValueFormWallet(undefined);
    setValueFormBank(undefined);
    setStepNewBeneficiary(null);
    setBankSelected(null);
    setWalletSkip(false);
    setBankSkip(false);
  };

  const handleStep = (step: number) => {
    if (loadingCreateCompany) return;
    setStepNewBeneficiary(step);
  };

  const handleBack = () => {
    if (loadingCreateCompany) return;
    if (!stepNewBeneficiary) return;
    setStepNewBeneficiary(stepNewBeneficiary - 1);
  };

  const handleSkip = () => {
    if (loadingCreateCompany) return;
    if (!stepNewBeneficiary) return;
    setStepNewBeneficiary(stepNewBeneficiary + 1);
  };

  const onSubmitInfo = (values: InfoBeneficiary) => {
    if (!stepNewBeneficiary) return;
    setStepNewBeneficiary(stepNewBeneficiary + 1);
    setValueFormInfo(values);
  };

  const onSubmitWallet = (values: WalletBeneficiary) => {
    if (!stepNewBeneficiary) return;
    setStepNewBeneficiary(stepNewBeneficiary + 1);
    setValueFormWallet(values);
    setWalletSkip(false);
  };

  const onSubmitBank = async (values: any) => {
    if (!stepNewBeneficiary) return;
    setStepNewBeneficiary(stepNewBeneficiary + 1);
    setValueFormBank(values);
    setBankSkip(false);
  };

  const onSubmit = async () => {
    if (loadingCreateCompany) return;
    if (!valueFormInfo) return;
    const infoBody = {
      fullName: valueFormInfo.companyName || "",
      name: valueFormInfo.nickName || "",
      directorFirstName: valueFormInfo.directorFirstName || "",
      directorLastName: valueFormInfo.directorLastName || "",
      address1: valueFormInfo.address1 || "",
      address2: valueFormInfo.address2 || "",
      city: valueFormInfo.city || "",
      stateProvince: valueFormInfo.stateProvince || "",
      country: valueFormInfo.companyOfficiallyRegistered || "",
      tinVatUid: valueFormInfo.tinVatUid || "",
      regNo: valueFormInfo.regNo || "",
      phoneNumber1: valueFormInfo.phoneNumber1 || "",
      email: valueFormInfo.email || "",
      smartUID: valueFormInfo.smartUID ? valueFormInfo.smartUID : undefined,
      website: valueFormInfo.website || "",
      postalCode: valueFormInfo.postalCode || "",
      refUserId: infoByUserCode?.id ? infoByUserCode?.id : undefined,
    };

    const companyWallet = {
      assetConfigGroupId: valueFormWallet?.currency || 0,
      networkId: valueFormWallet?.network || 0,
      address: valueFormWallet?.address || "",
      name: valueFormWallet?.name || "",
    };

    const bank = !valueFormBank?.bankId
      ? {
        name: valueFormBank?.bankName || "",
        swift: valueFormBank?.bankSwift || "",
        legalAddress: valueFormBank?.bankAddress || "",
        country: valueFormBank?.bankCountry || "",
      }
      : undefined;

    const bankAccount = {
      ...(valueFormBank?.bankId ? { bankId: valueFormBank?.bankId } : {}),
      currencyCode: valueFormBank?.currencyCode || "",
      abaRoutingNumber: valueFormBank?.abaRoutingNumber && valueFormBank?.abaRoutingNumber !== '' ? valueFormBank?.abaRoutingNumber : undefined,
      bankStateBranchNumber: valueFormBank?.bankStateBranchNumber && valueFormBank?.bankStateBranchNumber !== '' ? valueFormBank?.bankStateBranchNumber : undefined,
      branchNumber: valueFormBank?.branchNumber || "",
      accountNumber: valueFormBank?.accountNumber || "",
    };

    const companyBody = {
      ...infoBody,
      ...(valueFormWallet && !walletSkip ? { companyWallet } : {}),
      ...(valueFormBank && bank && !bankSkip ? { bank } : {}),
      ...(valueFormBank && !bankSkip ? { bankAccount } : {}),
    };
    createCompany(companyBody);
  };

  const handleCancelDetail = () => {
    navigate("/home");
  };

  const handleCancelFutureDate = () => {
    formFutureDate?.setFieldsValue({
      futurePaymentDate: undefined,
    })
    setActionFutureDate(false);
  };

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;
    if (canAction) setTitleHeader("Send Money");
    else {
      if (openNotification) {
        openNotification?.({
          key: "not_permission",
          message:
            "Your account is currently under review by the admin. You will be able to use this feature once the review is complete and your account is verified.",
          type: "error",
        });
      }
      navigate(`/`);
    }
  }, []);

  useEffect(() => {
    if (!detailSendMoney) return;
    const handleBalanceUpdate = () => {
      getDetailSendMoney(detailSendMoney?.id);
    };
    socket.on(
      `user.paymentRequest.${detailSendMoney?.id}.updated`,
      handleBalanceUpdate
    );

    return () => {
      socket.off(
        `user.paymentRequest.${detailSendMoney?.id}.updated`,
        handleBalanceUpdate
      );
    };
  }, [socket, detailSendMoney]);

  // Clear edit mode when navigating to send-money without orderId and no current detail (e.g. from menu)
  useEffect(() => {
    if (!orderId && !detailSendMoney) setEditData(null);
  }, [orderId, detailSendMoney]);

  // Sync editData & detailSendMoney from URL orderId: when user opens /send-money?orderId=364
  // or when order detail is refetched (e.g. after director-transfer updates fees)
  useEffect(() => {
    if (!orderId || !dataSendMoney?.data) return;
    const order = dataSendMoney.data;
    setEditData(order);
    setDetailSendMoney(order);
    if (order?.beneficiary?.id) {
      setDefaultBeneficiary(order.beneficiary.id);
    }
  }, [orderId, dataSendMoney?.data]);

  // When edit mode: find beneficiary company by name and set selection + form field
  useEffect(() => {
    if (!editData?.beneficiary?.companyName || !dataListCompany?.items?.length) return;
    const name = editData.beneficiary.companyName;
    const company = (dataListCompany.items as any[]).find(
      (item: any) => item?.fullName === name || item?.companyName === name || item?.name === name
    );
    if (company) {
      setBeneficiarySelected(company);
      formSendMoney?.setFieldValue("beneficiary", company.id);
    }
  }, [editData?.beneficiary?.companyName, dataListCompany?.items, formSendMoney]);

  useEffect(() => {
    if (!dataAllCurrency?.length) return;
    if (orderId) {
      if (orderId && dataSendMoney?.data) {
        const order = dataSendMoney.data;
        const receiveSymbol = order.receiveCurrency;
        const paymentSymbol = order.paymentCurrency;
        const beneficiaryCurrencyDefault = dataAllCurrency.find((item: any) => item.symbol === receiveSymbol) as any;
        const paymentCurrencyDefault = dataAllCurrency.find((item: any) => item.symbol === paymentSymbol) as any;
        const beneficiaryCurrencyId = beneficiaryCurrencyDefault?.id;
        const paymentCurrencyId = paymentCurrencyDefault?.id;
        if (beneficiaryCurrencyId !== null) setSendCurrency(beneficiaryCurrencyId);
        if (paymentCurrencyId !== null) setPaymentCurrency(paymentCurrencyId);
        return;
      }
      return;
    }

    let beneficiaryCurrency = 'USD';
    let paymentCurrency = 'EUR';
    if (currencyDefault) {
      beneficiaryCurrency = currencyDefault === 'USD' ? 'EUR' : 'USD';
      paymentCurrency = currencyDefault;
    }
    const beneficiaryCurrencyDefault = dataAllCurrency.find((item: any) => item.symbol === beneficiaryCurrency) as any;
    const paymentCurrencyDefault = dataAllCurrency.find((item: any) => item.symbol === paymentCurrency) as any;
    const beneficiaryCurrencyId = beneficiaryCurrencyDefault?.id;
    const paymentCurrencyId = paymentCurrencyDefault?.id;
    setSendCurrency(beneficiaryCurrencyId);
    setPaymentCurrency(paymentCurrencyId);
  }, [dataAllCurrency, orderId, dataSendMoney?.data]);

  const handleEdit = () => {
    const id = detailSendMoney?.id;
    if (!id) return;
    // Use latest order (e.g. after Update Order) so document list is in sync and we don't re-send same documentChanges.new
    setEditData(detailSendMoney);
    setOpenModalDirectorTransfer(false);
    navigate(`/send-money?orderId=${id}`, { replace: false });
    setReloadRateFeeKey((prev) => prev + 1);
  }

  // Refetch latest order detail for director-transfer modal (do not touch form-transfer-v2)
  const handleRefetchDetailSendMoneyForModal = (id: number | string) => {
    if (!id) return;
    navigate(`/send-money?orderId=${id}`, { replace: false });
    refetchDetailSendMoney();
  }

  const handleRefetchDetailSendMoney = (id: string) => {
    if (!id) return;
    navigate(`/send-money?orderId=${id}`, { replace: false });
    refetchDetailSendMoney();
  }

  return (
    <div className="send-money-container">
      <CustomBreadCrumb breadCrumb={breadCrumb} showHome={true} />
      <FormTransferV2
        orderId={orderId || undefined}
        editData={editData}
        setEditData={setEditData}
        loadingListWallet={loadingListWallet}
        loadingListBankAccount={loadingListBankAccount}
        form={formSendMoney}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        setStepNewBeneficiary={setStepNewBeneficiary}
        dataListCompany={dataListCompany}
        paramsBeneficiary={paramsBeneficiary}
        setParamsBeneficiary={setParamsBeneficiary}
        beneficiarySelected={beneficiarySelected}
        setBeneficiarySelected={setBeneficiarySelected}
        handleChangeBeneficiary={() => {
          setDefaultBeneficiaryWalletId(null);
          setDefaultBeneficiaryBankAccountId(null);
          setWalletSelected(null);
          setBankAccountSelected(null);
        }}
        dataListWallet={dataListWalletWithDefault}
        paramsWallet={paramsWallet}
        setParamsWallet={setParamsWallet}
        walletSelected={walletSelected}
        setWalletSelected={setWalletSelected}
        dataListBankAccount={dataListBankAccountWithDefault}
        paramsBankAccount={paramsBankAccount}
        setParamsBankAccount={setParamsBankAccount}
        bankAccountSelected={bankAccountSelected}
        setBankAccountSelected={setBankAccountSelected}
        sendCurrency={sendCurrency}
        setSendCurrency={setSendCurrency}
        paymentCurrency={paymentCurrency}
        setPaymentCurrency={setPaymentCurrency}
        dataCompaniesDefault={dataCompaniesDefault}
        companyDefaultSelected={companyDefaultSelected}
        setCompanyDefaultSelected={setCompanyDefaultSelected}
        setSearchDefaultCompany={setSearchDefaultCompany}
        paramsWalletProvider={paramsWalletProvider}
        setParamsWalletProvider={setParamsWalletProvider}
        walletSelectedProvider={walletSelectedProvider}
        setWalletSelectedProvider={setWalletSelectedProvider}
        dataListWalletProvider={dataListWalletProvider}
        paramsBankAccountProvider={paramsBankAccountProvider}
        setParamsBankAccountProvider={setParamsBankAccountProvider}
        bankAccountSelectedProvider={bankAccountSelectedProvider}
        setBankAccountSelectedProvider={setBankAccountSelectedProvider}
        dataListBankAccountProvider={dataListBankAccountProvider}
        getDetailSendMoney={getDetailSendMoney}
        setOpenAddWallet={setOpenAddWallet}
        setOpenAddBankAccount={setOpenAddBankAccount}
        loadingDetail={loadingDetail}
        socket={socket}
        companyDefaultCurrency={companyDefaultCurrency}
        loadingCompanyCurrency={loadingCompanyCurrency}
        loadingDefaulCompany={loadingDefaulCompany}
        dataAllCurrency={dataAllCurrency}
        onChangeDisabledPortfolio={setDisabledPortfolio}
        reloadRateFeeKey={reloadRateFeeKey}
      />
      <Modal
        open={!!openModalDriectorTransfer}
        onCancel={handleCancelDetail}
        footer={null}
        destroyOnClose
        maskClosable={false}
        title={
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <Typography
                style={{ fontSize: 20, fontWeight: 700, color: "#C2C6CE" }}
              >
                {`Order Created Successfully`}
              </Typography>
              <PrinterIcon
                className="icon-animation"
                style={{ cursor: "pointer" }}
                onClick={() =>
                  window.open(detailSendMoney?.printableFormUrl, "_blank")
                }
              />
            </div>
            <CloseIcon
              onClick={handleCancelDetail}
              className="icon-animation"
            />
          </>
        }
        className="send-money-modal"
      >
        <DirectorTransferDetailV2
          detailSendMoney={detailSendMoney}
          setActionFutureDate={setActionFutureDate}
          handleEdit={handleEdit}
          paymentCurrency={paymentCurrency}
          handleRefetchDetailSendMoney={handleRefetchDetailSendMoneyForModal}
        />
      </Modal>
      <Modal
        open={!!stepNewBeneficiary}
        onCancel={handleCloseModalBeneficiary}
        footer={null}
        destroyOnClose
        maskClosable={false}
        title={
          <TitleModal
            stepNewBeneficiary={stepNewBeneficiary}
            listStep={listStep}
            handleStep={handleStep}
            handleCloseModalBeneficiary={handleCloseModalBeneficiary}
          />
        }
        className="add-beneficiary-modal"
      >
        <div className="add-beneficiary-modal-body">
          <FormInfo
            onSubmitInfo={onSubmitInfo}
            stepNewBeneficiary={stepNewBeneficiary}
            metadataCountries={metadataCountries}
            valueFormInfo={valueFormInfo}
            setInfoByUserCode={setInfoByUserCode}
            infoByUserCode={infoByUserCode}
          />
          <FormWallet
            onSubmitWallet={onSubmitWallet}
            valueFormWallet={valueFormWallet}
            onBack={handleBack}
            onSkip={() => {
              setWalletSkip(true);
              handleSkip();
            }}
            stepNewBeneficiary={stepNewBeneficiary}
          />
          <FormBank
            onSubmitBank={onSubmitBank}
            valueFormBank={valueFormBank}
            onBack={handleBack}
            onSkip={() => {
              setBankSkip(true);
              handleSkip();
            }}
            bankSelected={bankSelected}
            setBankSelected={setBankSelected}
            stepNewBeneficiary={stepNewBeneficiary}
            metadataCountries={metadataCountries}
            metadataCurrency={metadataCurrency}
          />
          <FormConfirm
            onSubmit={onSubmit}
            onBack={handleBack}
            stepNewBeneficiary={stepNewBeneficiary}
            walletSkip={walletSkip}
            bankSkip={bankSkip}
            valueFormInfo={valueFormInfo}
            valueFormWallet={valueFormWallet}
            valueFormBank={valueFormBank}
            bankSelected={bankSelected}
            setStepNewBeneficiary={setStepNewBeneficiary}
            loadingCreateCompany={loadingCreateCompany}
          />
        </div>
      </Modal>
      <FormAddWallet
        openAddWallet={openAddWallet}
        setOpenAddWallet={setOpenAddWallet}
        refectListWallet={refectListWallet}
        beneficiarySelected={beneficiarySelected}
        formSendMoney={formSendMoney}
        sendCurrency={sendCurrency}
      />
      <FormAddBankAccount
        openAddBankAccount={openAddBankAccount}
        setOpenAddBankAccount={setOpenAddBankAccount}
        refectListBankAccount={refectListBankAccount}
        beneficiarySelected={beneficiarySelected}
        formSendMoney={formSendMoney}
        sendCurrency={sendCurrency}
        dataAllCurrency={dataAllCurrency}
      />
      <Modal
        open={actionFutureDate}
        onCancel={handleCancelFutureDate}
        footer={null}
        destroyOnClose
        maskClosable={false}
        title={
          <>
            <Typography
              style={{
                fontSize: isTablet ? 16 : 20,
                fontWeight: 700,
                color: "#C2C6CE",
              }}
            >
              {"Future date"}
            </Typography>
            <CloseIcon onClick={handleCancelFutureDate} className="icon-animation" />
          </>
        }
        className="edit-wallet-modal"
      >
        <FormFutureDate
          detailSendMoney={detailSendMoney}
          form={formFutureDate}
          handleCancel={handleCancelFutureDate}
          id={detailSendMoney?.id}
          handleRefetch={handleRefetchDetailSendMoney}
          notNavigate={true}
        />
      </Modal>
    </div>
  );
};
